import {
  LinkButton,
  ArrowsRightLeftIcon,
  ArrowDownCircleIcon,
  BoltSlashIcon,
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ListSkeleton
} from '@/components';
import {
  CurrencyValue,
  EmptyBox,
  Flex,
  List,
  ListHeader,
  ListItem,
  RelativeDateTime
} from '@blockanalitica/ui';
import { ReactNode } from 'react';
import { useApi, PaginatedResponse } from '@/api';
import styled from 'styled-components';

type EventMapItem = {
  label: string;
  icon: ReactNode;
  color: string;
};

const EVENT_MAP: Record<string, EventMapItem> = {
  Borrow: {
    label: 'Borrow',
    icon: <ArrowTrendingDownIcon color="#3B82F6" />,
    color: '#3B82F6'
  },
  LiquidationCall: {
    label: 'Liquidation Call',
    icon: <BoltSlashIcon color="#EF4444" />,
    color: '#EF4444'
  },
  Repay: {
    label: 'Repay',
    icon: <ArrowPathIcon color="#6366F1" />,
    color: '#6366F1'
  },
  Supply: {
    label: 'Supply',
    icon: <ArrowTrendingUpIcon color="#22C55E" />,
    color: '#22C55E'
  },
  Withdraw: {
    label: 'Withdraw',
    icon: <ArrowDownCircleIcon color="#F59E0B" size="2" />,
    color: '#F59E0B'
  },
  BalanceTransfer: {
    label: 'Balance Transfer',
    icon: <ArrowsRightLeftIcon color="#14B8A6" />,
    color: '#14B8A6'
  }
};

const ActivityIcon = styled(Flex)`
  width: ${(props) => props.theme.sizes['4']};
  height: ${(props) => props.theme.sizes['4']};
  border-radius: 50%;
  align-items: center;
  justify-content: center;

  ${(props) => {
    const color = props.color || props.theme.colors.primary;
    return `
      background-color: ${color}20;
      color: ${color};
    `;
  }}
`;

const ActivityTitle = styled.div`
  font-size: ${(props) => props.theme.sizes['0']};
  color: ${(props) => props.theme.colors.text};
`;

const ListItemEvent = styled(ListItem)`
  padding: ${(props) => props.theme.space['2xs']};
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
`;

export type BaseActivityItem = {
  order_index: string;
  event: string;
  datetime: string;
  amount: string;
  amount_usd: string;
};

interface RecentActivityProps<T extends BaseActivityItem> {
  url: string;
  linkTo: string;
  contentFn?: (item: T) => ReactNode;
}

export default function RecentActivity<T extends BaseActivityItem>({
  url,
  linkTo,
  contentFn
}: RecentActivityProps<T>) {
  const [events, eventsError] = useApi<PaginatedResponse<T[]>>({
    url,
    queryParams: {
      days_ago: 7,
      limit: 3,
      page: 1
    }
  });

  if (eventsError) throw eventsError;

  if (!events) {
    return <ListSkeleton />;
  }

  return (
    <List>
      <ListHeader
        title="Recent Activity"
        actions={[
          <LinkButton
            size="small"
            to={linkTo}
            key="viewAllButtonRecentActivity">
            View All
          </LinkButton>
        ]}
      />

      {events.results.length > 0 ? (
        events.results.map((item) => (
          <ListItemEvent
            variant="recentActivityItem"
            // @ts-ignore
            key={`${item.order_index}_${item.wallet_address}`}>
            <Flex gap="s" alignItems="center" justifyContent="space-between">
              <Flex gap="s" alignItems="center">
                <ActivityIcon color={EVENT_MAP[item.event]?.color}>
                  {EVENT_MAP[item.event]?.icon}
                </ActivityIcon>
                <Flex gap="4xs" flexDirection="column">
                  <ActivityTitle>
                    {contentFn ? contentFn(item) : item.event}
                  </ActivityTitle>
                  <Flex gap="xs" color="muted" fontSize="-1">
                    <RelativeDateTime value={item.datetime} />
                  </Flex>
                </Flex>
              </Flex>
              <Flex gap="4xs" flexDirection="column" alignItems="flex-end">
                <CurrencyValue value={Math.abs(Number(item.amount))} />
                <CurrencyValue
                  color="muted"
                  fontSize="-1"
                  value={Math.abs(Number(item.amount_usd))}
                  options={{
                    currency: 'USD'
                  }}
                />
              </Flex>
            </Flex>
          </ListItemEvent>
        ))
      ) : (
        <EmptyBox message="No activity in the past 7 days" />
      )}
    </List>
  );
}
